import * as React from "react";
import { Check } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * The one checkbox in the product.
 *
 * ⚠️ A checkbox, NOT a switch, and the distinction is deliberate rather than cosmetic. A switch
 * is a setting that takes effect; a checkbox picks members of a set. Every remaining call site
 * is the second kind — sensitivity clearances, permission grants, which columns to draw, which
 * rows are selected — and `RolesPanel` says why in its own words: clearance is a SET, not a
 * level, and a control implying otherwise would hand EMR agents access to HR requests.
 *
 * ⚠️ A REAL `<input type="checkbox">` underneath, not a styled button. That keeps label
 * association, form participation, `indeterminate`, and the browser's own keyboard handling —
 * all of which a `role="checkbox"` div has to reimplement and usually reimplements wrongly. It
 * also means this needed no new dependency: `appearance-none` plus a `peer-checked` glyph is
 * the whole of it.
 *
 * The props are the input's, so every swap from a bare `<input type="checkbox">` was
 * like-for-like — `checked`, `onChange` with `e.target.checked`, `disabled` and `className` all
 * behave exactly as they did.
 */
const Checkbox = React.forwardRef<
  HTMLInputElement,
  Omit<React.ComponentPropsWithoutRef<"input">, "type">
>(({ className, ...props }, ref) => (
  <span className={cn("relative inline-flex h-4 w-4 shrink-0", className)}>
    <input
      type="checkbox"
      ref={ref}
      className={cn(
        "peer h-4 w-4 shrink-0 cursor-pointer appearance-none rounded-[5px] border border-input bg-background",
        "transition-colors duration-150 ease-out",
        "hover:border-ring/60",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "checked:border-primary checked:bg-primary",
        "indeterminate:border-primary indeterminate:bg-primary",
        "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-input",
      )}
      {...props}
    />

    {/* The tick. `pointer-events-none` so every click lands on the input beneath it, and
        `peer-checked` rather than a React branch so the two can never disagree about state. */}
    <Check
      aria-hidden
      strokeWidth={3.5}
      className={cn(
        "pointer-events-none absolute left-0 top-0 h-4 w-4 p-[1.5px] text-primary-foreground",
        "scale-75 opacity-0 transition duration-150 ease-out",
        "peer-checked:scale-100 peer-checked:opacity-100",
      )}
    />
  </span>
));
Checkbox.displayName = "Checkbox";

export { Checkbox };
